import { Theme } from "@mui/material";
import { useLxTheme } from "./useLxTheme";

/**
 * Function to access the LX colors
 *
 * See also the `useLxColor` hook.
 *
 * @package Theme
 * @example
 * function MyStyledComponent = styled(Box)( ({theme}) => {
 *    const colors = lxColor(theme)
 *    return {
 *       backgroundColor:  colors.action.focus
 *    }
 * })
 */

export function lxColor(theme: Theme) {
  return {
    primary: {
      ...theme.palette.primary,
      background: "#eeee",
    },
    secondary: {
      ...theme.palette.secondary,
      background: "#eeee",
    },
    success: {
      ...theme.palette.success,
      text: "#053F22",
      background: "#e6f5ee",
    },
    info: {
      ...theme.palette.info,
      text: "#12364E",
      background: "#eaf3f9",
    },
    warning: {
      ...theme.palette.warning,
      text: "#603200",
      background: "#fdf2e5",
    },
    error: {
      ...theme.palette.error,
      text: "#591613",
      background: "#fcebea",
    },
    greyscale: { ...theme.palette.grey },
    text: { ...theme.palette.text },
    action: { ...theme.palette.action },
    misc: {
      black: theme.palette.common.black,
      white: theme.palette.common.white,
      paper: theme.palette.background?.paper,
      default: theme.palette.background?.default,
      divider: theme.palette.divider,
      backdrop: "rgba(0, 0, 0, 0.9)",
      filledInput: "rgba(0, 0, 0, 0.09)",
      tooltip: "#6F6F6F",
      snackbar: "#424242",
      ratingFull: "#FFAE57",
      ratingGradient: "linear-gradient(90deg, #FFAE57 50.51%, #B5B5B5 50.52%)",
    },
    pageBackground: theme.palette.grey[100],
  };
}

/**
 * useLxColor - A hook to access all colors
 *
 * @package Theme
 * @example
 * function MyActionBox() {
 *   const colors = useLxColor()
 *   const actionFocusColor  = colors.action.focus;
 *
 *   return <Box sx={{ backgroundColor: actionFocusColor }}>Action!</Box>
 * }
 */
export const useLxColor = () => {
  const theme = useLxTheme();
  return lxColor(theme);
};
